<template>
  <div class="d-flex flex-column">
    <input
      id="image"
      type="file"
      accept="image/*"
      capture="environment"
      @change="handleFileInput"
      style="display: none"
    />
    <v-btn
      :disabled="disabled"
      :loading="loading"
      rounded
      color="#067b26"
      class="button white--text"
      @click="take"
      v-if="!evidenceSelected.content_url"
    >
      Capturar Imagem
    </v-btn>
  </div>
</template>
<script>
import PedidoService from "@/services/pedidos.service";
import GlobalStore from "@/store/modules/global";
import piexif from "piexifjs";

export default {
  name: "CapturePhotoMobile",
  props: {
    evidenceSelected: {
      type: Object,
    },
    geolocation: {
      type: Object
    }
  },
  data: () => ({
    photo: "",
    loading: false,
    disabled: false,
  }),
  methods: {
    take() {
      document.getElementById("image").click();
    },

    addMetadataToBlob(blob, coords, isHorizontal) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => {
          const dataUrl = reader.result;

          // Data/hora no formato EXIF "YYYY:MM:DD HH:MM:SS"
          const now = new Date();
          const dateTime = `${now.getFullYear()}:${String(
            now.getMonth() + 1
          ).padStart(2, "0")}:${String(now.getDate()).padStart(2, "0")} ${String(
            now.getHours()
          ).padStart(2, "0")}:${String(now.getMinutes()).padStart(
            2,
            "0"
          )}:${String(now.getSeconds()).padStart(2, "0")}`;

          // Monta EXIF
          const exifObj = {
            "0th": {              
              [piexif.ImageIFD.Model]: navigator.userAgent,
              [piexif.ImageIFD.DateTime]: dateTime,   
              [piexif.ImageIFD.Orientation]: 1           
            },
            "Exif": {
              [piexif.ExifIFD.DateTimeOriginal]: dateTime,
              [piexif.ExifIFD.DateTimeDigitized]: dateTime
            },
            "GPS": {
              [piexif.GPSIFD.GPSLatitude]: this.decToDms(coords.latitude),
              [piexif.GPSIFD.GPSLatitudeRef]: coords.latitude >= 0 ? "N" : "S",
              [piexif.GPSIFD.GPSLongitude]: this.decToDms(coords.longitude),
              [piexif.GPSIFD.GPSLongitudeRef]: coords.longitude >= 0 ? "E" : "W"
            }
          };

          // Injeta EXIF
          const exifBytes = piexif.dump(exifObj);
          const newDataUrl = piexif.insert(exifBytes, dataUrl);

          // Converte DataURL de volta para Blob/File
          const arr = newDataUrl.split(",")[1];
          const binary = atob(arr);
          const buffer = new Uint8Array(binary.length);
          for (let i = 0; i < binary.length; i++) {
            buffer[i] = binary.charCodeAt(i);
          }

          const newBlob = new Blob([buffer], { type: "image/jpeg" });
          const newFile = new File([newBlob], blob.name, { type: "image/jpeg" });
          resolve(newFile);
        };

        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    },

    // Converte decimal para formato DMS usado em EXIF
    decToDms(dec) {
      dec = Math.abs(dec);
      const deg = Math.floor(dec);
      const minFloat = (dec - deg) * 60;
      const min = Math.floor(minFloat);
      const sec = Math.round((minFloat - min) * 60);
      return [[deg, 1], [min, 1], [sec, 1]]; // formato EXIF fracionário
    },

    async handleFileInput(event) {
      const original = event.target.files[0];
      if (!original) return;

      this.disabled = true;
      this.loading = true;

      try {
        // No iOS o File da câmera é só uma referência a um arquivo temporário,
        // que pode ser descartado antes do envio (body chega vazio no backend).
        // Copiar os bytes para memória imediatamente evita isso.
        const file = await this.snapshotFile(original);

        if (!file.size) {
          this.resetInput(event);
          return GlobalStore.setSnackBar({
            show: true,
            type: "error",
            message: "Falha ao ler a imagem, capture novamente",
          });
        }

        const { key, kind: { name } } = this.evidenceSelected;
        const filename = `${name}.${file.type.split("/")[1] || "jpeg"}`;

        const isHorizontal = await this.checkOrientation(file);
        if (!isHorizontal) {
          this.resetInput(event);
          return GlobalStore.setSnackBar({
            show: true,
            type: "error",
            message: "A imagem deve ser tirada na horizontal",
          });
        }

        const position = await this.getCurrentLocation();

        const formData = new FormData();
        formData.append('file', file, filename)
        formData.append('latitude', position.latitude);
        formData.append('longitude', position.longitude);
        formData.append('taken_at', new Date().toISOString());
        formData.append('camera', navigator.userAgent);

        PedidoService.upload(key, formData).subscribe(
          (res) => {
            window.location.reload();
          },
          (error) => {
            this.resetInput(event);
            GlobalStore.setSnackBar({
                show: true,
                type: "error",
                message: "não foi possível enviar, tente novamente",
            });
          }
        );
      } catch (err) {
        this.resetInput(event);
        GlobalStore.setSnackBar({
          show: true,
          type: "error",
          message: "Não foi possível enviar. Verifique a permissão de localização e tente novamente",
        });
      }
    },

    snapshotFile(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () =>
          resolve(new File([reader.result], file.name, { type: file.type || "image/jpeg" }));
        reader.onerror = () => reject(reader.error);
        reader.readAsArrayBuffer(file);
      });
    },

    resetInput(event) {
      event.target.value = null;
      this.disabled = false;
      this.loading = false;
    },

    checkOrientation(file) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          resolve(img.width > img.height); // true = horizontal
        };
        img.onerror = reject;
        img.src = URL.createObjectURL(file);
      });
    },
    
    getCurrentLocation() {
      return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          return reject(new Error("Geolocalização não disponível"));
        }

        navigator.geolocation.getCurrentPosition(
          (pos) => resolve(pos.coords),
          (err) => reject(err),
          { enableHighAccuracy: true }
        );
      });
    },
  },
};
</script>
